import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { ErrorBoundary } from "@/components/error-boundary";
import { AuthProvider } from "@/lib/auth-provider";
import { I18nProvider } from "@/i18n";
import { Download } from "lucide-react";
import { useInstallPrompt } from "@/hooks/use-install-prompt";

const LandingPage = lazy(() => import("@/pages/landing"));
import { ProtectedRoute, AdminProtectedRoute } from "@/components/protected-route";
import { useAuth } from "@/lib/auth-provider";

/** Show landing page for visitors, redirect to dashboard for authenticated users */
function RootRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <Suspense fallback={null}><LandingPage /></Suspense>;
}
import { DashboardLayout } from "@/layouts/dashboard-layout";
import LoginPage from "@/routes/login";
import SignupPage from "@/routes/signup";
import ForgotPasswordPage from "@/routes/forgot-password";
import AcceptInvitationPage from "@/routes/accept-invitation";
import OnboardingPage from "@/routes/onboarding";
import DashboardPage from "@/routes/dashboard";
import CrmPage from "@/routes/crm/index";
import EmailPage from "@/routes/email/index";
import PagesPage from "@/routes/pages/index";
import WorkflowsPage from "@/routes/workflows/index";
import InboxPage from "@/routes/inbox/index";
import InboxStatsPage from "@/routes/inbox/stats";
import CannedResponsesPage from "@/routes/inbox/canned-responses";
import AiPage from "@/routes/ai/index";
import AnalyticsPage from "@/routes/analytics/index";
import ServicesPage from "@/routes/services/index";
import EstimateBuilderPage from "@/routes/services/estimates";
import BookingCapacityPage from "@/routes/services/booking-capacity";
import FieldDispatchPage from "@/routes/services/dispatch";
import ProposalListPage from "@/routes/offers/proposal-list";
import ProposalDetailPage from "@/routes/offers/proposal-detail";
import ProposalPrintPage from "@/routes/offers/proposal-print";
import WebinarsPage from "@/routes/webinars/index";
import CommunicationsPage from "@/routes/communications/index";
import SettingsIndex from "@/routes/settings/index";
import SettingsLayout from "@/routes/settings/layout";
import ProfilePage from "@/routes/settings/profile";
import SecuritySettingsPage from "@/routes/settings/security";
import TeamPage from "@/routes/settings/team";
import BillingPage from "@/routes/settings/billing";
import FeedbackPage from "@/routes/settings/feedback";
import EcommercePage from "@/routes/settings/ecommerce";
import ApiKeysPage from "@/routes/settings/api-keys";
import NotificationsPage from "@/routes/settings/notifications";
import IntegrationsPage from "@/routes/settings/integrations";
import DomainsPage from "@/routes/settings/domains";
import AuditLogPage from "@/routes/settings/audit-log";
import WebhooksPage from "@/routes/settings/webhooks";
import CollaborationsPage from "@/routes/settings/collaborations";
import DomainPurchasePage from "@/routes/settings/domain-purchase";
import DomainDnsPage from "@/routes/settings/domain-dns";
import MarketplacePage from "@/routes/settings/marketplace";
import ComplianceDashboardPage from "@/routes/settings/compliance/index";
import ConsentDashboardPage from "@/routes/settings/compliance/consent";
import AuditLogCompliancePage from "@/routes/settings/compliance/audit-log";
import CookieConsentBuilderPage from "@/routes/settings/compliance/cookies";
import DataIsolationPage from "@/routes/settings/compliance/isolation";
import DeleteRequestPage from "@/routes/settings/compliance/delete-request";
import DsarPage from "@/routes/settings/compliance/dsar";
import BrandRegistrationPage from "@/routes/settings/messaging/ten-dlc/brand-registration";
import CampaignRegistrationPage from "@/routes/settings/messaging/ten-dlc/campaign-registration";
import TenDlcStatusDashboard from "@/routes/settings/messaging/ten-dlc/status-dashboard";
import TcpaCompliancePage from "@/routes/settings/messaging/tcpa/index";
import AffiliateDashboardPage from "@/routes/affiliate/index";
import KitBuilderPage from "@/routes/affiliate/kit-builder";
import AgencyHierarchyPage from "@/routes/admin/agencies/index";
import WhitelabelConfigPage from "@/routes/admin/agencies/whitelabel";
import SubAccountManagementPage from "@/routes/admin/agencies/sub-accounts";
import UrgencyQueuePage from "@/routes/admin/agencies/urgency-queue";
import ProofOfValuePage from "@/routes/admin/agencies/proof-of-value";
import { AdminLayout } from "@/layouts/admin-layout";
import AdminOverviewPage from "@/routes/admin/index";
import AdminTenantsPage from "@/routes/admin/tenant-migration";
import AdminUsersPage from "@/routes/admin/users";
import AdminAccessRequestsPage from "@/routes/admin/access-requests";
import AdminHealthPage from "@/routes/admin/health";
import AdminFlagsPage from "@/routes/admin/flags";
import AdminAiAgentsPage from "@/routes/admin/ai-agents";
import AdminAiAccountabilityPage from "@/routes/admin/ai-accountability";
import AdminFeatureFlagsPage from "@/routes/admin/feature-flags/index";
import AdminProvisionPage from "@/routes/admin/provision";
import AdminBillingPage from "@/routes/admin/billing";
import AdminTaxRatesPage from "@/routes/admin/tax-rates";
import AdminAnalyticsPage from "@/routes/admin/analytics";
import TrialFunnelPage from "@/routes/admin/trial-funnel";
import AdminMarketplaceReviewPage from "@/routes/admin/marketplace-review";
import AgencyDashboardPage from "@/routes/agency/dashboard";
import AgencyManagePage from "@/routes/agency/manage";
import AgencyAttributionPage from "@/routes/agency/attribution";
import AgencyFirstClientPage from "@/routes/agency/first-client";
import AgencyActivateClientPage from "@/routes/agency/activate-client";
import WebinarThankYouPage from "@/routes/webinars/thank-you";
import WaitingRoomPage from "@/routes/webinars/waiting-room";
import BackstagePage from "@/routes/webinars/backstage";
import LivePresenterPage from "@/routes/webinars/live-presenter";
import LiveAttendeePage from "@/routes/webinars/live-attendee";
import ReplayPage from "@/routes/webinars/replay";
import WebinarAnalyticsPage from "@/routes/webinars/analytics";
import NotFoundPage from "@/routes/not-found";
import PermissionDeniedPage from "@/routes/permission-denied";
import MigrationWizardPage from "@/pages/settings/migration/index";
import MigrationHistoryPage from "@/pages/settings/migration/history";
import { OfflineBanner } from "@/components/offline-banner";
import { PersonaProvider } from "@/providers/persona-context";
import { DensityProvider } from "@/providers/density-context";
import { PersonaPrompt } from "@/components/persona/persona-prompt";
import { RateLimitToastProvider } from "@/components/rate-limit-toast";
import { SessionExpiredProvider } from "@/components/session-expired-modal";
import PricingPage from "@/pages/pricing/index";
import { MountedSurfaceGate } from "@/components/truth/mounted-surface-gate";

// === Stream B Sprint 16: Orphaned page wiring ===
import { BriefingPage } from "@/pages/briefing/index";
import ReviewDashboard from "@/pages/reputation/review-dashboard";
import ReviewCampaignsPage from "@/routes/reputation/review-campaigns";
import CommunityPage from "@/routes/community/index";
import CoursesPage from "@/routes/courses/index";
import TemplateBrowser from "@/pages/onboarding/template-browser";
import BrandVoicePage from "@/pages/onboarding/brand-voice";
import GuidedSetupPage from "@/pages/onboarding/guided-setup";
import WebsiteCrawlPage from "@/routes/onboarding/website-crawl";
import ConfigPreviewPage from "@/pages/onboarding/config-preview";
import CompletionScore from "@/pages/onboarding/completion-score";
import McpSettingsPage from "@/routes/settings/mcp-settings";

// === Stream B Sprint 20: Localization + Content Editor + Layout Preferences ===
import LocalizationPage from "@/routes/settings/localization";
import MultiLanguageContentEditor from "@/routes/communications/content-editor";
import LayoutPreferencesPage from "@/routes/settings/layout-preferences";

// === Stream B CRM-T.5: Email Sync ===
import EmailSyncSettingsPage from "@/routes/settings/email-sync";
import CustomFieldsSettingsPage from "@/routes/settings/custom-fields";

// === Stream B Sprint 21: Competitive Audit Tool + Admin Analytics ===
import AuditPage from "@/pages/audit/index";
import AuditAnalyticsPage from "@/pages/admin/audit-analytics";

// === Display pages — full-screen, auto-refresh, display-token auth ===
import WaitingRoomDisplay from "@/pages/displays/waiting-room";
import WalkInQueueDisplay from "@/pages/displays/walk-in-queue";
import FrontDeskDisplay from "@/pages/displays/front-desk";
import LeaderboardDisplay from "@/pages/displays/leaderboard";
import KioskCheckInDisplay from "@/pages/displays/kiosk-checkin";

// === Stream B Sprint 18: Service Delivery + Portal + Displays ===
import { PortalLayout } from "@/layouts/portal-layout";
import PortalDashboard from "@/pages/portal/index";
import PortalCourses from "@/pages/portal/courses";
import PortalHistory from "@/pages/portal/history";
import PortalMessages from "@/pages/portal/messages";
import ScoringSettingsPage from "@/routes/settings/scoring";
import EnrichmentSettingsPage from "@/routes/settings/integrations/enrichment";
import BookingEmbedPage from "@/routes/settings/booking-embed";
import CalendarSyncPage from "@/routes/settings/calendar-sync";

// === Stream C Sprint 17: Creative Engine screens ===
import FormIntelligence from "@/pages/page-builder/form-intelligence";
import DynamicVariants from "@/pages/page-builder/dynamic-variants";
import CompetitorDna from "@/pages/page-builder/competitor-dna";

// === Stream C Sprint 18: Reputation + Social + Marketplace + Franchise ===
import ReviewIntelligence from "@/pages/reputation/review-intelligence";
import EscalationQueuePage from "@/pages/reputation/escalation-queue";
import SocialListening from "@/pages/reputation/social-listening";
import ContentCalendar from "@/pages/reputation/content-calendar";
import UgcLibrary from "@/pages/reputation/ugc-library";
import ReviewWidget from "@/pages/settings/reputation/review-widget";
import MarketplaceBrowserPage from "@/pages/marketplace/browser";
import CreatorDashboardPage from "@/pages/marketplace/creator-dashboard";
import MarketplaceDetailPage from "@/pages/marketplace/detail";
import HqPushCenter from "@/pages/admin/franchise/hq-push";
import AgencyDirectoryPage from "@/pages/marketplace/agency-directory";
import FranchiseBenchmarks from "@/pages/admin/franchise/benchmarks";

// === Stream C Sprint DS: AI Bar + Density + Spotlight + Notifications ===
import DensitySettingsPage from "@/pages/settings/display/density-settings";

// === Stream C Sprint 19: Content Morphing + Social Listening + Intent ===
import ContentMorphingPage from "@/pages/social/content-morphing";
import SocialPublishingPage from "@/pages/social/social-publishing";
import CompetitorMonitoringPage from "@/pages/reputation/competitor-monitoring";
import PurchaseIntentPage from "@/pages/reputation/purchase-intent";

// === Stream C Sprint 20: UGC + Local SEO + GBP ===
import LocalSeoDashboard from "@/pages/reputation/local-seo";
import GbpSyncPage from "@/pages/reputation/gbp-sync";

// === Stream C Sprint 21: Service Intelligence ===
// === Stream C: Form Builder ===
import FormBuilder from "@/routes/forms/builder";
import FormEmbedPage from "@/routes/forms/embed";
import PublicFormPage from "@/routes/forms/public-form";
import PublicEstimatePage from "@/routes/public-estimate";
import PublicProposalSignPage from "@/routes/public-proposal-sign";
import PublicReviewPage from "@/routes/public-review";
import LegalDocumentPage from "@/routes/legal";

// === Stream C: Analytics + Knowledge Base ===
import WebsiteReport from "@/pages/analytics/website-report";
import VisitorIntelligence from "@/pages/analytics/visitor-intelligence";
import KnowledgeBasePage from "@/routes/settings/ai/knowledge-base";

// === Stream C Sprint 16: Messaging + Integration + Brand + Admin + Agency ===
import ChannelManagement from "@/pages/settings/messaging/channels";
import SmsConfigWizard from "@/pages/settings/messaging/sms-config";
import WhatsAppConfigWizard from "@/pages/settings/messaging/whatsapp-config";
import TelegramConfigPage from "@/pages/settings/messaging/telegram-config";
import ChannelHealthPage from "@/pages/settings/messaging/channel-health";
import ProviderRoutingRules from "@/pages/settings/messaging/routing";
import WhatsAppTemplatesPage from "@/routes/settings/messaging/whatsapp-templates";
import MessengerConfigPage from "@/pages/settings/messaging/messenger-config";
import InstagramConfigPage from "@/pages/settings/messaging/instagram-config";
import IntegrationDashboard from "@/pages/settings/integrations/dashboard";
import SyncConfigPage from "@/pages/settings/integrations/sync-config";
import BrandKitPage from "@/pages/settings/brand/index";
import DeploymentDashboardPage from "@/routes/admin/deployments";
import ImpersonationPage from "@/routes/admin/impersonation";
import AdminOperationsPage from "@/routes/admin/operations";
import RevenueDashboardPage from "@/pages/admin/agencies/billing/revenue-dashboard";
import UsageDashboardPage from "@/pages/admin/agencies/billing/usage-dashboard";
import CommissionLedgerPage from "@/pages/admin/agencies/billing/commission-ledger";
import WholesalePricingPage from "@/pages/admin/agencies/billing/wholesale-pricing";
import MarginCalculatorPage from "@/pages/admin/agencies/billing/margin-calculator";
import BlogListPage from "@/routes/cms/blog-list";
import BlogEditorPage from "@/routes/cms/blog-editor";
import BlogDetailPage from "@/routes/cms/blog-detail";
import MetaConnectPage from "@/routes/settings/integrations/meta-connect";

// === SOC-004: Unified content calendar ===
import CalendarUnifiedPage from "@/routes/content/CalendarUnified";

// === SOC-004: Social Platform Calendar (cross-platform month grid) ===
import SocialCalendarRoute from "@/routes/social/calendar";

function PwaInstallBanner() {
  const { isInstallable, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);

  if (!isInstallable || dismissed) {
    return null;
  }

  return (
    <div className="fixed right-4 bottom-16 z-50 max-w-sm rounded-2xl border border-border bg-card/95 p-4 shadow-lg backdrop-blur">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
          <Download className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Install stero.ai</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add the app to your home screen for faster launch and offline shell support.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { void promptInstall(); }}
              className="inline-flex min-h-[44px] items-center rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
            >
              Install app
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="inline-flex min-h-[44px] items-center rounded-full px-3 py-2 text-sm text-muted-foreground transition hover:text-foreground"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EaseScoreDetailsGate() {
  return (
    <MountedSurfaceGate
      title="Ease Score details are not mounted as a source of truth"
      description="The detailed score breakdown page was still computing local sample data and overclaiming accuracy."
      evidence="The dashboard gauge stays mounted through the live `/crm/intelligence/ease-score` contract, but the old detail page was driven by mock factors and random trend generation."
      liveLinks={[
        {
          label: "Back to Dashboard",
          to: "/dashboard",
          description: "Use the mounted dashboard gauge backed by the CRM intelligence endpoint.",
        },
        {
          label: "Team Settings",
          to: "/settings/team",
          description: "Improve real setup data instead of reading a fake score breakdown.",
        },
        {
          label: "Integrations",
          to: "/settings/integrations",
          description: "Open the real integrations surface that contributes to readiness.",
        },
        {
          label: "Pages",
          to: "/pages/sites",
          description: "Work on live page assets instead of the retired score explainer.",
        },
      ]}
    />
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <I18nProvider>
        <AuthProvider>
          <PersonaProvider>
            <BrowserRouter>
              <RateLimitToastProvider>
                <SessionExpiredProvider>
                  <DensityProvider>
                    <Routes>
                      {/* Landing page for visitors, dashboard redirect for authenticated users */}
                      <Route path="/" element={<RootRoute />} />
                      <Route path="/landing" element={<Navigate to="/" replace />} />

            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/permission-denied" element={<ProtectedRoute><PermissionDeniedPage /></ProtectedRoute>} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<LegalDocumentPage documentKey="privacy" />} />
            <Route path="/terms" element={<LegalDocumentPage documentKey="terms" />} />
            <Route path="/cookies" element={<LegalDocumentPage documentKey="cookies" />} />
            <Route path="/legal/dpa" element={<LegalDocumentPage documentKey="dpa" />} />
            <Route path="/f/:formId" element={<PublicFormPage />} />
            <Route path="/estimate/:tenantSlug/:proposalId" element={<PublicEstimatePage />} />
            <Route path="/sign/:tenantSlug/:proposalId" element={<PublicProposalSignPage />} />
            <Route path="/offers/:proposalId/print" element={<ProtectedRoute><ProposalPrintPage /></ProtectedRoute>} />
            <Route path="/review/:shareToken" element={<PublicReviewPage />} />
            <Route path="/audit" element={<AuditPage />} />

            {/* Onboarding — protected but outside dashboard layout */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route path="/onboarding/templates" element={<ProtectedRoute><TemplateBrowser /></ProtectedRoute>} />
            <Route path="/onboarding/website-crawl" element={<ProtectedRoute><WebsiteCrawlPage /></ProtectedRoute>} />
            <Route path="/onboarding/guided-setup" element={<ProtectedRoute><GuidedSetupPage /></ProtectedRoute>} />
            <Route path="/onboarding/brand-voice" element={<ProtectedRoute><BrandVoicePage /></ProtectedRoute>} />
            <Route path="/onboarding/preview" element={<ProtectedRoute><ConfigPreviewPage /></ProtectedRoute>} />
            <Route path="/onboarding/setup" element={<Navigate to="/onboarding" replace />} />
            <Route path="/onboarding/completion" element={<ProtectedRoute><CompletionScore /></ProtectedRoute>} />

            {/* Dashboard — protected with sidebar layout */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="crm/*" element={<CrmPage />} />
              <Route path="email/*" element={<EmailPage />} />
              <Route path="pages/*" element={<PagesPage />} />
              {/* Stream C Sprint 17: Creative Engine screens */}
              <Route path="pages/forms/:formId/intelligence" element={<FormIntelligence />} />
              <Route path="pages/:pageId/variants" element={<DynamicVariants />} />
              <Route path="pages/competitor-dna" element={<CompetitorDna />} />
              <Route path="workflows/*" element={<WorkflowsPage />} />
              <Route path="inbox" element={<InboxPage />} />
              <Route path="inbox/stats" element={<InboxStatsPage />} />
              <Route path="inbox/canned-responses" element={<CannedResponsesPage />} />
              <Route path="ai/*" element={<AiPage />} />
              <Route path="analytics/*" element={<AnalyticsPage />} />
              <Route path="analytics/website-report" element={<WebsiteReport />} />
              <Route path="analytics/visitors" element={<VisitorIntelligence />} />
              <Route path="services/*" element={<ServicesPage />} />
              <Route path="offers" element={<ProposalListPage />} />
              <Route path="offers/:proposalId" element={<ProposalDetailPage />} />
              <Route path="webinars/*" element={<WebinarsPage />} />
              <Route path="communications/*" element={<CommunicationsPage />} />
              <Route path="communications/content-editor" element={<MultiLanguageContentEditor />} />
              {/* SOC-004: Unified content calendar */}
              <Route path="content/calendar" element={<CalendarUnifiedPage />} />
              {/* SOC-004: Social platform month-grid calendar (audit row, all
                  social channels in one view) */}
              <Route path="social/calendar" element={<SocialCalendarRoute />} />
              <Route path="cms/blog" element={<BlogListPage />} />
              <Route path="cms/blog/new" element={<BlogEditorPage />} />
              <Route path="cms/blog/:postId/edit" element={<BlogEditorPage />} />
              <Route path="cms/blog/:slug" element={<BlogDetailPage />} />
              <Route path="reputation" element={<ReviewDashboard />} />
              {/* Stream C Sprint 18: Reputation + Social sub-routes */}
              <Route path="reputation/campaigns" element={<ReviewCampaignsPage />} />
              <Route path="reputation/reviews" element={<ReviewIntelligence />} />
              <Route path="reputation/escalations" element={<EscalationQueuePage />} />
              <Route path="reputation/social" element={<SocialListening />} />
              <Route path="reputation/social/calendar" element={<ContentCalendar />} />
              <Route path="reputation/social/publish" element={<SocialPublishingPage />} />
              <Route path="reputation/social/ugc" element={<UgcLibrary />} />
              {/* Stream C Sprint 19: Content Morphing + Competitor + Intent */}
              <Route path="reputation/social/morph" element={<ContentMorphingPage />} />
              <Route path="reputation/social/competitors" element={<CompetitorMonitoringPage />} />
              <Route path="reputation/social/intent" element={<PurchaseIntentPage />} />
              {/* Stream C Sprint 20: Local SEO + GBP */}
              <Route path="reputation/seo" element={<LocalSeoDashboard />} />
              <Route path="reputation/gbp" element={<GbpSyncPage />} />
              {/* Legacy service-intelligence screens were removed from the shell until real APIs exist */}
              <Route path="services/capacity" element={<BookingCapacityPage />} />
              <Route path="services/dispatch" element={<FieldDispatchPage />} />
              <Route path="services/packages" element={<Navigate to="/services/unavailable" replace />} />
              <Route path="services/estimates" element={<EstimateBuilderPage />} />
              {/* Stream C: Form Builder */}
              <Route path="forms/builder/:formId" element={<FormBuilder />} />
              <Route path="forms/builder/new" element={<FormBuilder />} />
              <Route path="forms/embed" element={<FormEmbedPage />} />
              {/* Stream C Sprint 18: Marketplace routes */}
              <Route path="marketplace" element={<MarketplaceBrowserPage />} />
              <Route path="marketplace/:packageId" element={<MarketplaceDetailPage />} />
              <Route path="marketplace/creator" element={<CreatorDashboardPage />} />
              <Route path="marketplace/directory" element={<AgencyDirectoryPage />} />
              <Route path="briefing" element={<BriefingPage />} />
              <Route path="community/*" element={<CommunityPage />} />
              <Route path="courses/*" element={<CoursesPage />} />
              <Route path="settings" element={<SettingsLayout />}>
                <Route index element={<SettingsIndex />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="security" element={<SecuritySettingsPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="feedback" element={<FeedbackPage />} />
                <Route path="ecommerce" element={<EcommercePage />} />
                <Route path="api-keys" element={<ApiKeysPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="domains" element={<DomainsPage />} />
                <Route path="webhooks" element={<WebhooksPage />} />
                <Route path="collaborations" element={<CollaborationsPage />} />
                <Route path="marketplace" element={<MarketplacePage />} />
                <Route path="domain-purchase" element={<DomainPurchasePage />} />
                <Route path="domain-dns" element={<DomainDnsPage />} />
                <Route path="audit-log" element={<AuditLogPage />} />
                <Route path="compliance" element={<ComplianceDashboardPage />} />
                <Route path="compliance/consent" element={<ConsentDashboardPage />} />
                <Route path="compliance/audit-log" element={<AuditLogCompliancePage />} />
                <Route path="compliance/cookies" element={<CookieConsentBuilderPage />} />
                <Route path="compliance/isolation" element={<DataIsolationPage />} />
                <Route path="compliance/delete" element={<DeleteRequestPage />} />
                <Route path="compliance/dsar" element={<DsarPage />} />
                <Route path="messaging/10dlc/brand" element={<BrandRegistrationPage />} />
                <Route path="messaging/10dlc/campaigns" element={<CampaignRegistrationPage />} />
                <Route path="messaging/10dlc/status" element={<TenDlcStatusDashboard />} />
                <Route path="messaging/tcpa" element={<TcpaCompliancePage />} />
                <Route path="ease-score" element={<EaseScoreDetailsGate />} />
                <Route path="migration" element={<MigrationWizardPage />} />
                <Route path="migration/history" element={<MigrationHistoryPage />} />
                {/* C1: Messaging channel pages */}
                <Route path="messaging/channels" element={<ChannelManagement />} />
                <Route path="messaging/sms" element={<SmsConfigWizard />} />
                <Route path="messaging/whatsapp" element={<WhatsAppConfigWizard />} />
                <Route path="messaging/whatsapp/templates" element={<WhatsAppTemplatesPage />} />
                <Route path="messaging/instagram" element={<InstagramConfigPage />} />
                <Route path="messaging/messenger" element={<MessengerConfigPage />} />
                <Route path="messaging/telegram" element={<TelegramConfigPage />} />
                <Route path="messaging/health" element={<ChannelHealthPage />} />
                <Route path="messaging/routing" element={<ProviderRoutingRules />} />
                {/* C2: Integration + Brand pages */}
                <Route path="integrations/hub" element={<IntegrationDashboard />} />
                <Route path="integrations/meta-connect" element={<MetaConnectPage />} />
                <Route path="integrations/sync" element={<SyncConfigPage />} />
                <Route path="brand" element={<BrandKitPage />} />
                <Route path="mcp" element={<McpSettingsPage />} />
                <Route path="localization" element={<LocalizationPage />} />
                <Route path="layout-preferences" element={<LayoutPreferencesPage />} />
                <Route path="email-sync" element={<EmailSyncSettingsPage />} />
                <Route path="custom-fields" element={<CustomFieldsSettingsPage />} />
                <Route path="bookings/embed" element={<BookingEmbedPage />} />
                <Route path="calendar" element={<CalendarSyncPage />} />
                {/* C5: Review collection widget config */}
                <Route path="reputation/widget" element={<ReviewWidget />} />
                {/* C59: Knowledge Base */}
                <Route path="ai/knowledge-base" element={<KnowledgeBasePage />} />
                {/* B65: Contact enrichment providers */}
                <Route path="integrations/enrichment" element={<EnrichmentSettingsPage />} />
                {/* A9: Lead scoring builder */}
                <Route path="scoring" element={<ScoringSettingsPage />} />
                {/* C5: Density display settings */}
                <Route path="display" element={<DensitySettingsPage />} />
              </Route>
            </Route>

            {/* Affiliate Dashboard */}
            <Route path="affiliate" element={<ProtectedRoute><AffiliateDashboardPage /></ProtectedRoute>} />
            <Route path="affiliate/kits" element={<ProtectedRoute><KitBuilderPage /></ProtectedRoute>} />

            {/* Platform Admin — separate layout, dark theme, admin role gate */}
            <Route path="admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
              <Route index element={<AdminOverviewPage />} />
              <Route path="tenants" element={<AdminTenantsPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="access-requests" element={<AdminAccessRequestsPage />} />
              <Route path="health" element={<AdminHealthPage />} />
              <Route path="flags" element={<AdminFlagsPage />} />
              <Route path="ai-agents" element={<AdminAiAgentsPage />} />
              <Route path="ai-accountability" element={<AdminAiAccountabilityPage />} />
              <Route path="feature-flags" element={<AdminFeatureFlagsPage />} />
              <Route path="provision" element={<AdminProvisionPage />} />
              <Route path="billing" element={<AdminBillingPage />} />
              <Route path="tax-rates" element={<AdminTaxRatesPage />} />
              <Route path="analytics" element={<AdminAnalyticsPage />} />
              <Route path="trial-funnel" element={<TrialFunnelPage />} />
              <Route path="marketplace-review" element={<AdminMarketplaceReviewPage />} />
              <Route path="agencies" element={<AgencyHierarchyPage />} />
              <Route path="agencies/whitelabel" element={<WhitelabelConfigPage />} />
              <Route path="agencies/sub-accounts" element={<SubAccountManagementPage />} />
              <Route path="agencies/urgency" element={<UrgencyQueuePage />} />
              <Route path="agencies/proof-of-value" element={<ProofOfValuePage />} />
              {/* C2: Admin pages */}
              <Route path="deployments" element={<DeploymentDashboardPage />} />
              <Route path="impersonation" element={<ImpersonationPage />} />
              <Route path="operations" element={<AdminOperationsPage />} />
              {/* C8/C10: Franchise admin pages */}
              <Route path="franchise/push" element={<HqPushCenter />} />
              <Route path="franchise/benchmarks" element={<FranchiseBenchmarks />} />
              {/* B6: Audit analytics */}
              <Route path="audit-analytics" element={<AuditAnalyticsPage />} />
              {/* C3: Agency billing sub-pages */}
              <Route path="agencies/billing/revenue" element={<RevenueDashboardPage />} />
              <Route path="agencies/billing/usage" element={<UsageDashboardPage />} />
              <Route path="agencies/billing/commissions" element={<CommissionLedgerPage />} />
              <Route path="agencies/billing/wholesale" element={<WholesalePricingPage />} />
              <Route path="agencies/billing/margin" element={<MarginCalculatorPage />} />
            </Route>

            {/* Agency routes — B.13/B.14/B.15 */}
            <Route path="agency" element={<ProtectedRoute><AgencyDashboardPage /></ProtectedRoute>} />
            <Route path="agency/first-client" element={<ProtectedRoute><AgencyFirstClientPage /></ProtectedRoute>} />
            <Route path="agency/activate" element={<ProtectedRoute><AgencyActivateClientPage /></ProtectedRoute>} />
            <Route path="agency/manage/:clientId" element={<ProtectedRoute><AgencyManagePage /></ProtectedRoute>} />
            <Route path="agency/attribution" element={<ProtectedRoute><AgencyAttributionPage /></ProtectedRoute>} />

            {/* Client Portal — buyer-facing, own layout (no sidebar).
                Uses PortalLayout with minimal header + nav. */}
            <Route path="portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
              <Route index element={<PortalDashboard />} />
              <Route path="courses" element={<PortalCourses />} />
              <Route path="courses/:courseRef" element={<PortalCourses />} />
              <Route path="courses/:courseRef/lessons/:lessonRef" element={<PortalCourses />} />
              <Route path="history" element={<PortalHistory />} />
              <Route path="messages" element={<PortalMessages />} />
            </Route>

            {/* Webinar viewer routes — outside DashboardLayout intentionally.
                These are full-screen experiences (live stream, waiting room, replay)
                that must not have sidebar chrome. Management routes are inside
                DashboardLayout at /webinars/*. */}
            <Route path="webinar/thank-you" element={<WebinarThankYouPage />} />
            <Route path="webinar/:webinarId/waiting" element={<WaitingRoomPage />} />
            <Route path="webinar/:webinarId/backstage" element={<BackstagePage />} />
            <Route path="webinar/:webinarId/live" element={<LivePresenterPage />} />
            <Route path="webinar/:webinarId/watch" element={<LiveAttendeePage />} />
            <Route path="webinar/:webinarId/analytics" element={<ProtectedRoute><WebinarAnalyticsPage /></ProtectedRoute>} />
            <Route path="webinar/:webinarId/replay" element={<ReplayPage />} />

            {/* Display pages — full-screen, auto-refresh, no sidebar.
                Used for office displays, kiosks, and walk-in queue screens.
                Display-token auth handled internally (not user session). */}
            <Route path="display/walk-in" element={<WalkInQueueDisplay />} />
            <Route path="display/waiting-room" element={<WaitingRoomDisplay />} />
            <Route path="display/front-desk" element={<FrontDeskDisplay />} />
            <Route path="display/leaderboard" element={<LeaderboardDisplay />} />
            <Route path="display/kiosk" element={<KioskCheckInDisplay />} />

                      {/* 404 catch-all — D.2 */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>

                    {/* Offline detection banner — D.2 */}
                    <OfflineBanner />
                    <PwaInstallBanner />

                    {/* B2: First-login persona prompt — shows when persona not set */}
                    <PersonaPrompt />
                  </DensityProvider>
                </SessionExpiredProvider>
              </RateLimitToastProvider>
            </BrowserRouter>
          </PersonaProvider>
        </AuthProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}
